import { Chess, Square } from 'chess.js';
import { Color, Key } from 'chessground/types';
import { stockfishEngine, EngineEval } from '../utils/StockfishEngine';
import { whiteWinPct } from './winChance';
import { MoveType } from '../types/MoveType';
import { CorrectionOutcome, LinePosition } from './CorrectionBoard';

// Grading a "correct your mistakes" attempt off the board: CorrectionBoard's handleMove logic,
// extracted so the game overlay can practise on its own board (GamesListItemChessboard). Keep the two
// in sync.

export function classifyDrop(drop: number): MoveType {
  if (drop >= 20) return MoveType.Blunder;
  if (drop >= 10) return MoveType.Mistake;
  if (drop >= 5) return MoveType.Inaccuracy;
  if (drop >= 2) return MoveType.Good;
  return MoveType.Best;
}

// Good or better: nothing meaningful given up, so the swing isn't worth showing.
export function isGoodOrBetter(moveType: MoveType): boolean {
  return (
    moveType === MoveType.Brilliant ||
    moveType === MoveType.Great ||
    moveType === MoveType.Best ||
    moveType === MoveType.Excellent ||
    moveType === MoveType.Good ||
    moveType === MoveType.Book
  );
}

// SAN of a UCI move from a position; null if it doesn't parse.
export function uciToSan(fen: string, uci: string): string | null {
  try {
    const c = new Chess(fen);
    const m = c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] || 'q' });
    return m?.san ?? null;
  } catch {
    return null;
  }
}

// How much room the position gave, for the second hint: legal moves and how many are good. Counts come
// from the server (the same pair as the gauge); without them the legal moves are counted here and the
// good count is unknown.
export function moveRoom(
  fen: string,
  goodMoves: number | null | undefined,
  legalMoves: number | null | undefined,
): { legal: number; good: number | null } | null {
  let legal = legalMoves ?? null;
  if (legal == null) {
    try {
      legal = new Chess(fen).moves().length;
    } catch {
      return null;
    }
  }
  if (legal <= 0) return null;
  return { legal, good: goodMoves ?? null };
}

// The move between two positions, found by trying the legal moves. The repair queue arrives as bare
// FENs, so this gives the last-move highlight and the intro move. Compared on placement + side to
// move only, since move counters differ between generators.
export function moveBetween(fromFen: string, toFen: string): [string, string] | null {
  const target = toFen.split(' ').slice(0, 2).join(' ');
  try {
    const c = new Chess(fromFen);
    for (const mv of c.moves({ verbose: true })) {
      c.move(mv);
      const reached = c.fen().split(' ').slice(0, 2).join(' ');
      c.undo();
      if (reached === target) return [mv.from, mv.to];
    }
  } catch {
    return null;
  }
  return null;
}

const PIECE_NAMES: Record<string, string> = {
  p: 'pawn',
  n: 'knight',
  b: 'bishop',
  r: 'rook',
  q: 'queen',
  k: 'king',
};

// The square the engine's move starts from, for the last hint. From the stored best move, so instant.
export function solutionPiece(
  fen: string,
  uci: string | null | undefined,
): { square: Key; name: string } | null {
  if (uci == null || uci.length < 4) return null;
  const square = uci.slice(0, 2);
  try {
    const piece = new Chess(fen).get(square as Square);
    if (!piece) return null;
    return { square: square as Key, name: PIECE_NAMES[piece.type] ?? 'piece' };
  } catch {
    return null;
  }
}

// White's win chance for an engine result. Both fields: mate 0 carries the winner in the score's sign.
// See EngineEval.
function whiteWinOf(e: EngineEval): number {
  return whiteWinPct(e.scoreCp, e.mateIn);
}

function moverWin(e: EngineEval, sideToMove: Color): number {
  const white = whiteWinOf(e);
  return sideToMove === 'white' ? white : 100 - white;
}

// Play a UCI line out from a FEN, collecting each position and its numbered SAN for the best-line
// chips. Capped to stay glanceable.
export function buildForward(
  fen: string,
  uci: string[],
  max = 8,
): { positions: LinePosition[]; plies: { san: string; numberLabel: string | null }[] } {
  const positions: LinePosition[] = [];
  const plies: { san: string; numberLabel: string | null }[] = [];
  try {
    const c = new Chess(fen);
    for (let i = 0; i < Math.min(uci.length, max); i++) {
      const t = uci[i];
      if (t.length < 4) break;
      const number = c.moveNumber();
      const white = c.turn() === 'w';
      const mv = c.move({ from: t.slice(0, 2), to: t.slice(2, 4), promotion: t[4] || 'q' });
      if (mv == null) break;
      positions.push({ fen: c.fen(), lastMove: [mv.from, mv.to] });
      plies.push({
        san: mv.san,
        numberLabel: white ? `${number}.` : i === 0 ? `${number}…` : null,
      });
    }
  } catch {
    return { positions: [], plies: [] };
  }
  return { positions, plies };
}

export type GradeResult = {
  outcome: CorrectionOutcome;
  // Position after the played move and the best line from there. Empty line when ungraded.
  afterFen: string;
  bestLineUci: string[];
  // White's win chance after the move, for the eval bar. Null when ungraded.
  afterWhiteWin: number | null;
  // First move of that line as squares, for the arrow coloured by the grade. Null without a line.
  firstLineArrow: [Key, Key] | null;
};

// Grade one attempted move from the solve position, as CorrectionBoard.handleMove does. The exact game
// move keeps the server's verdict. Null only when the move is illegal.
export async function gradeMove(
  solveFen: string,
  orig: string,
  dest: string,
  sideToMove: Color,
  solution: string, // server's stored best move (UCI), the fallback with no engine
  gamePlayedSan: string,
  gameMoveType: MoveType,
): Promise<GradeResult | null> {
  const probe = new Chess(solveFen);
  let played;
  try {
    played = probe.move({ from: orig, to: dest, promotion: 'q' });
  } catch {
    return null;
  }
  if (played == null) return null;
  const playedUci = `${orig}${dest}`;
  const afterFen = probe.fen();
  const isSameAsGame = played.san === gamePlayedSan;

  const build = (
    correct: boolean,
    dropPct: number | null,
    moveType: MoveType | null,
    bestMoveSan: string | null,
    bestLineUci: string[],
    afterWhiteWin: number | null,
  ): GradeResult => {
    const forward = buildForward(afterFen, bestLineUci);
    const first = forward.positions[0]?.lastMove ?? null;
    return {
      outcome: { correct, playedUci, playedSan: played!.san, dropPct, moveType, bestMoveSan },
      afterFen,
      bestLineUci,
      afterWhiteWin,
      firstLineArrow: first ? [first[0] as Key, first[1] as Key] : null,
    };
  };

  // Checkmate is graded off the board: nothing beats mate, and the engine can't search a finished
  // position.
  if (probe.isCheckmate() && !isSameAsGame) {
    return build(true, 0, MoveType.Best, played.san, [], sideToMove === 'white' ? 100 : 0);
  }

  // No engine in this browser: exact match against the server's best move only.
  if (!stockfishEngine.isSupported()) {
    const ok = solution.slice(0, 4) === playedUci;
    return build(
      ok,
      null,
      isSameAsGame ? gameMoveType : null,
      uciToSan(solveFen, solution),
      [],
      null,
    );
  }

  try {
    const [best, after] = await Promise.all([
      stockfishEngine.evaluate(solveFen),
      stockfishEngine.evaluate(afterFen),
    ]);
    const drop = Math.round(moverWin(best, sideToMove) - moverWin(after, sideToMove));
    // Server precedence for the exact game move; otherwise trust the live grade of this position.
    const correct = isSameAsGame ? false : drop < 5;
    const dropPct = isSameAsGame ? null : drop;
    const moveType = isSameAsGame ? gameMoveType : classifyDrop(drop);
    return build(
      correct,
      dropPct,
      moveType,
      best.bestMove ? uciToSan(solveFen, best.bestMove) : null,
      after.pv,
      whiteWinOf(after),
    );
  } catch {
    // Engine unavailable or timed out: same exact-match fallback.
    const ok = solution.slice(0, 4) === playedUci;
    return build(
      ok,
      null,
      isSameAsGame ? gameMoveType : null,
      uciToSan(solveFen, solution),
      [],
      null,
    );
  }
}

// The second hint in words: how many moves the position allows and how many hold up. Shared by the
// repair session and the game overlay's practice panel.
export function roomHintText(room: { legal: number; good: number | null }): string {
  const { legal, good } = room;
  if (good == null) return `You have ${legal} legal moves here.`;
  if (good === 0) {
    return `You have ${legal} legal moves here, and none of them hold. Look for the one that gives up least.`;
  }
  return `You have ${legal} legal moves here, and ${
    good === 1 ? 'only 1 of them is' : `${good} of them are`
  } good.`;
}
